/**
 * Inclusive calendar-month count between booking start and end (UTC month indices).
 */
export function inclusiveBookingMonths(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return 0;
  }
  return Math.max(
    0,
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
      (end.getUTCMonth() - start.getUTCMonth()) +
      1
  );
}

export function meetsMinimumBookingDuration(startDate, endDate, minimumMonths) {
  const min = Number(minimumMonths);
  if (!Number.isInteger(min) || min < 1) return true;
  return inclusiveBookingMonths(startDate, endDate) >= min;
}

export function utcMonthIndex(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return NaN;
  return d.getUTCFullYear() * 12 + d.getUTCMonth();
}

/** UTC month index of the last month on the public availability calendar (inclusive). */
export function availabilityCalendarEndMonthIndex(today, months) {
  const n = Number(months);
  const todayIdx = utcMonthIndex(today);
  if (!Number.isFinite(todayIdx)) return NaN;
  if (!Number.isInteger(n) || n < 1) return todayIdx;
  return todayIdx + n - 1;
}

/** True when date's UTC month is on or before the last availability-calendar month. */
export function isMonthOnOrBeforeAvailabilityCalendar(date, today, months) {
  const idx = utcMonthIndex(date);
  const end = availabilityCalendarEndMonthIndex(today, months);
  if (!Number.isFinite(idx) || !Number.isFinite(end)) return false;
  return idx <= end;
}

/** YYYY-MM-01 for the last month on the availability calendar. */
export function availabilityCalendarEndMonthStartIso(today, months) {
  const end = availabilityCalendarEndMonthIndex(today, months);
  if (!Number.isFinite(end)) return '';
  const year = Math.floor(end / 12);
  const month = (end % 12) + 1;
  return `${year}-${String(month).padStart(2, '0')}-01`;
}

/** Check-in month may be the current calendar month or later (partial current month allowed). */
export function isCheckInMonthAllowed(checkInDate, today) {
  const checkInIdx = utcMonthIndex(checkInDate);
  const todayIdx = utcMonthIndex(today);
  if (!Number.isFinite(checkInIdx) || !Number.isFinite(todayIdx)) return false;
  return checkInIdx >= todayIdx;
}

export function meetsMaximumBookingDuration(startDate, endDate, maximumMonths) {
  const max = Number(maximumMonths);
  if (!Number.isInteger(max) || max < 1) return true;
  return inclusiveBookingMonths(startDate, endDate) <= max;
}

/**
 * Inclusive calendar months between the month after bookingEnd and the day before nextStart.
 */
export function gapMonthsAfterBookingBeforeNext(bookingEndDate, nextBookingStartDate) {
  if (!bookingEndDate) return 0;
  if (!nextBookingStartDate) return Number.MAX_SAFE_INTEGER;

  const endA = bookingEndDate instanceof Date ? bookingEndDate : new Date(bookingEndDate);
  const startB = nextBookingStartDate instanceof Date ? nextBookingStartDate : new Date(nextBookingStartDate);
  if (Number.isNaN(endA.getTime()) || Number.isNaN(startB.getTime())) return 0;

  const first = new Date(Date.UTC(endA.getUTCFullYear(), endA.getUTCMonth() + 1, 1));
  const last = new Date(startB);
  last.setUTCDate(last.getUTCDate() - 1);
  if (last < first) return 0;

  return Math.max(
    0,
    (last.getUTCFullYear() - first.getUTCFullYear()) * 12 +
      (last.getUTCMonth() - first.getUTCMonth()) +
      1
  );
}

export function canRenewBookingWithGap(bookingEndDate, nextBookingStartDate, minimumGapMonths) {
  const minGap = Number(minimumGapMonths);
  const required = Number.isInteger(minGap) && minGap >= 1 ? minGap : 1;
  return gapMonthsAfterBookingBeforeNext(bookingEndDate, nextBookingStartDate) >= required;
}

export function firstDayOfMonthAfterBookingEnd(bookingEndDate) {
  const lastEnd = bookingEndDate instanceof Date ? new Date(bookingEndDate) : new Date(bookingEndDate);
  lastEnd.setUTCHours(23, 59, 59, 999);
  const lastYear = lastEnd.getUTCFullYear();
  const lastMonth = lastEnd.getUTCMonth();
  const nextMonthYear = lastMonth === 11 ? lastYear + 1 : lastYear;
  const nextMonthZeroIndexed = lastMonth === 11 ? 0 : lastMonth + 1;
  return new Date(Date.UTC(nextMonthYear, nextMonthZeroIndexed, 1, 0, 0, 0, 0));
}

export function lastDayOfMonthBeforeNextStart(nextBookingStartDate) {
  if (!nextBookingStartDate) return null;
  const startB = nextBookingStartDate instanceof Date ? new Date(nextBookingStartDate) : new Date(nextBookingStartDate);
  if (Number.isNaN(startB.getTime())) return null;
  const dayBefore = new Date(startB);
  dayBefore.setUTCDate(dayBefore.getUTCDate() - 1);
  dayBefore.setUTCHours(23, 59, 59, 999);
  return dayBefore;
}
